<script setup lang="ts">
import { computed } from 'vue';
import Spinner from '../feedback/Spinner.vue';

const props = withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'soft' | 'ghost' | 'danger' | 'danger-soft' | 'success' | 'link';
    size?: 'sm' | 'md' | 'lg';
    block?: boolean;
    disabled?: boolean;
    loading?: boolean;
    type?: 'button' | 'submit' | 'reset';
  }>(),
  { variant: 'primary', size: 'md', block: false, disabled: false, loading: false, type: 'button' },
);

// A loading button is non-interactive; the spinner replaces the leading icon.
const isDisabled = computed(() => props.disabled || props.loading);
</script>

<template>
  <button
    :type="type"
    :disabled="isDisabled"
    class="lumi-btn"
    :class="[`lumi-btn--${size}`, `lumi-btn--${variant}`, { 'lumi-btn--block': block }]"
  >
    <Spinner v-if="loading" size="sm" />
    <span v-else-if="$slots.icon" class="lumi-btn__icon"><slot name="icon" /></span>
    <slot />
  </button>
</template>

<style>
/* Spec §6: 36 px, 10 px radius; primary espresso/cream (sand/espresso in dark),
   secondary white with the panel-edge ring, destructive brick. Every variant
   keeps its label colour on hover — only the ground moves. */
.lumi-btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;height:var(--control-h);padding:0 14px;border-radius:var(--radius-md);font-family:var(--font-body);font-size:var(--text-sm);font-weight:var(--weight-medium);line-height:1;cursor:pointer;transition:background-color var(--dur-fast) var(--ease-standard),border-color var(--dur-fast) var(--ease-standard),color var(--dur-fast) var(--ease-standard);text-decoration:none;border:1px solid transparent;white-space:nowrap;}
.lumi-btn:disabled{opacity:.5;cursor:not-allowed;}
.lumi-btn--sm{height:var(--control-h-sm);padding:0 10px;}
.lumi-btn--lg{height:var(--control-h-lg);padding:0 20px;font-size:var(--text-base);}
.lumi-btn--primary{background:var(--btn-primary-bg);border-color:var(--btn-primary-bg);color:var(--btn-primary-fg);}
.lumi-btn--primary:hover:not(:disabled){background:var(--accent-hover);border-color:var(--accent-hover);}
.lumi-btn--secondary{background:var(--surface-card);border-color:var(--panel-edge);color:var(--text-heading);}
.lumi-btn--secondary:hover:not(:disabled){background:var(--surface-wash);}
.lumi-btn--soft{background:var(--surface-wash);border-color:var(--pill-ring);color:var(--text-heading);}
.lumi-btn--soft:hover:not(:disabled){background:var(--accent-soft-2);border-color:var(--panel-edge);}
.lumi-btn--ghost{background:transparent;color:var(--text-secondary);}
.lumi-btn--ghost:hover:not(:disabled){background:var(--surface-wash);color:var(--text-heading);}
.lumi-btn--danger{background:var(--danger);border-color:var(--danger);color:var(--danger-fg);}
.lumi-btn--danger:hover:not(:disabled){background:var(--danger-hover);border-color:var(--danger-hover);}
/* Brick soft (spec annex note-mod-zeit2): the Freigaben "Ablehnen" tone — a
   reject is a decision, not a destructive act on this screen, so it reads
   quieter than the solid danger button. */
.lumi-btn--danger-soft{background:var(--danger-soft);border-color:var(--danger-soft);color:var(--danger-text);}
.lumi-btn--danger-soft:hover:not(:disabled){background:color-mix(in srgb, var(--danger-soft) 80%, var(--danger-text) 20%);}
.lumi-btn--success{background:var(--success);border-color:var(--success);color:var(--success-fg);}
.lumi-btn--success:hover:not(:disabled){background:var(--success-hover);border-color:var(--success-hover);}
/* Text-style action: no box, sits inline with copy; underline only on hover. */
.lumi-btn--link{background:transparent;border-color:transparent;color:var(--text-heading);padding-inline:4px;height:auto;line-height:1.5;}
.lumi-btn--link:hover:not(:disabled){text-decoration:underline;}
.lumi-btn--block{width:100%;}
.lumi-btn__icon{display:inline-flex;flex-shrink:0;width:1.15em;height:1.15em;}
.lumi-btn__icon svg{width:100%;height:100%;}
</style>
